import type { ItemRequest, ReceptorRequest, TipoDeComprobante } from '../api';

/**
 * Tablas de referencia de la emisión (KAN-145). Son las etiquetas humanas de
 * los Ids que el contrato deja como números — sacadas de la documentación de
 * `docs/openapi.yaml` (`ReceptorRequest.condicionIva`, `ItemRequest.alicuota`)
 * y de la tabla de ARCA — pero **los Ids mismos no se tipean a mano**: cada
 * `id` es del tipo que el contrato ya declara (`ReceptorRequest['docTipo']`,
 * etc.), así que agregar acá un Id que el backend no acepta es un error de
 * compilación, no un bug que aparece al emitir. Vivir en un módulo aparte, y
 * no adentro del formulario, es lo que permite testear "¿el 6 dice
 * Monotributo?" sin montar ningún componente.
 *
 * ⚠️ **La letra NO está acá.** No hay una función `letraSegun(condicionIva)`
 * porque la letra la decide el backend cruzando la condición del receptor con
 * la del **emisor** (el tenant), que el front no tiene. {@link letraDeTipo}
 * traduce el `tipo` que ya viene resuelto en la respuesta — es lectura, no
 * cálculo.
 */

/** `ReceptorRequest.docTipo`. */
export const TIPOS_DE_DOCUMENTO: readonly { id: ReceptorRequest['docTipo']; etiqueta: string }[] = [
  { id: 80, etiqueta: 'CUIT' },
  { id: 86, etiqueta: 'CUIL' },
  { id: 96, etiqueta: 'DNI' },
  { id: 99, etiqueta: 'Consumidor final (sin documento)' },
];

/**
 * `ReceptorRequest.condicionIva` (RG 5616, `FEParamGetCondicionIvaReceptor`).
 * Las marcadas **A** son las que dan Factura A cuando el emisor es RI —
 * incluye el monotributista, que desde el 1-jul-2021 (Ley 27.618 / RG 5003)
 * ya NO es B. Ver la tabla completa en la descripción de `receptor.condicionIva`
 * en `docs/openapi.yaml`.
 */
export const CONDICIONES_IVA_RECEPTOR: readonly { id: ReceptorRequest['condicionIva']; etiqueta: string }[] = [
  { id: 1, etiqueta: 'IVA Responsable Inscripto' },
  { id: 6, etiqueta: 'Responsable Monotributo' },
  { id: 13, etiqueta: 'Monotributista Social' },
  { id: 16, etiqueta: 'Monotributo Trabajador Independiente Promovido' },
  { id: 5, etiqueta: 'Consumidor Final' },
  { id: 4, etiqueta: 'IVA Sujeto Exento' },
  { id: 7, etiqueta: 'Sujeto No Categorizado' },
  { id: 8, etiqueta: 'Proveedor del Exterior' },
  { id: 9, etiqueta: 'Cliente del Exterior' },
  { id: 10, etiqueta: 'IVA Liberado – Ley N° 19.640' },
  { id: 15, etiqueta: 'IVA No Alcanzado' },
];

/** `ItemRequest.alicuota` (`FEParamGetTiposIva`) — el Id, no el porcentaje. */
export const ALICUOTAS: readonly { id: ItemRequest['alicuota']; etiqueta: string }[] = [
  { id: 3, etiqueta: '0%' },
  { id: 9, etiqueta: '2,5%' },
  { id: 8, etiqueta: '5%' },
  { id: 4, etiqueta: '10,5%' },
  { id: 5, etiqueta: '21%' },
  { id: 6, etiqueta: '27%' },
];

/** `EmitirRequest.concepto`. Con 2 o 3, ARCA exige período y vencimiento de pago. */
export const CONCEPTOS: readonly { id: 1 | 2 | 3; etiqueta: string }[] = [
  { id: 1, etiqueta: 'Productos' },
  { id: 2, etiqueta: 'Servicios' },
  { id: 3, etiqueta: 'Productos y servicios' },
];

/**
 * ¿El PDF va a exigir nombre y domicilio de este receptor? (KAN-173)
 *
 * Sí para todo receptor identificado (RI, monotributo, exento, no categorizado…),
 * en Factura A y en B por igual: RG 1415, Anexo II. **No** para el consumidor
 * final anónimo (`docTipo 99`, que no se ficha) ni para el consumidor final
 * identificado (condición IVA `5`), porque la norma solo se lo exige a partir de un
 * monto que el servicio no rastrea. Es la misma regla que aplica el backend al
 * generar el PDF: si divergen, o se frena de más acá o el PDF falla con `409`
 * después de emitir.
 *
 * `condicionIva` en `null` es "todavía no eligió": no se exige nada hasta saberlo.
 */
export function requiereFicha(
  docTipo: ReceptorRequest['docTipo'],
  condicionIva: number | null,
): boolean {
  return docTipo !== 99 && condicionIva !== null && condicionIva !== 5;
}

/**
 * La letra que **ya decidió el backend**, para mostrarla en el resultado.
 * `Comprobante.tipo` es el `CbteTipo` de ARCA: `1` Factura A, `6` Factura B,
 * `11` Factura C. Cualquier otro valor (no debería pasar) se muestra tal cual
 * para no ocultar una respuesta inesperada.
 */
export function letraDeTipo(tipo: number | null | undefined): string {
  switch (tipo) {
    case 1:
      return 'A';
    case 6:
      return 'B';
    case 11:
      return 'C';
    default:
      return tipo == null ? '—' : `tipo ${tipo}`;
  }
}

/**
 * Suma los `importeFinal` de los ítems, ignorando los que todavía no son un
 * número válido (fila a medio cargar). Desde KAN-191 lo que se carga es el
 * importe **con IVA** y el total del comprobante es exactamente esa suma, así
 * que esto **sí es el total que va a salir**. Lo que el front sigue sin
 * anticipar es cuánto de eso es IVA: depende de la letra (en clase C no se
 * discrimina), que sale de la condición fiscal del emisor, y esa no viaja en
 * `TenantResumen`. Ver la nota grande en `FormularioDeEmision`.
 */
export function totalCargado(items: readonly { importeFinal: string }[]): number {
  return items.reduce((suma, item) => {
    const valor = Number(item.importeFinal.replace(',', '.'));
    return Number.isFinite(valor) && valor > 0 ? suma + valor : suma;
  }, 0);
}

/**
 * Los tipos que el listado (KAN-146) deja filtrar, con su nombre en castellano.
 * Cada `id` es del tipo que declara el contrato ({@link TipoDeComprobante}):
 * poner acá un tipo que el backend no acepta es un error de compilación.
 *
 * La letra que aparece en cada etiqueta es **descriptiva del código de ARCA**,
 * no una que el front decida: `1` ES la Factura A. Lo que nunca se calcula es
 * la letra de un comprobante concreto — esa viene en el campo `letra` de la
 * respuesta. Ver {@link nombreDeComprobante}.
 */
export const TIPOS_DE_COMPROBANTE: readonly { id: TipoDeComprobante; etiqueta: string }[] = [
  { id: 1, etiqueta: 'Factura A' },
  { id: 6, etiqueta: 'Factura B' },
  { id: 11, etiqueta: 'Factura C' },
  { id: 3, etiqueta: 'Nota de crédito A' },
  { id: 8, etiqueta: 'Nota de crédito B' },
  { id: 13, etiqueta: 'Nota de crédito C' },
  { id: 2, etiqueta: 'Nota de débito A' },
  { id: 7, etiqueta: 'Nota de débito B' },
  { id: 12, etiqueta: 'Nota de débito C' },
];

/**
 * Cómo se nombra un comprobante ya emitido: `Factura A`, `Nota de crédito B`,
 * `Nota de débito A` (KAN-192).
 *
 * ⚠️ **La letra NO se deduce acá**: llega en el parámetro `letra`, que el
 * backend derivó del `CbteTipo` y publica como campo propio del contrato
 * (`ComprobanteResumen.letra`, `ComprobanteDetalle.letra`). Del `tipo` se
 * saca **solo** si es factura, nota de crédito o nota de débito, que es el otro eje del mismo
 * código y no una decisión fiscal del front.
 *
 * Por eso esto no usa {@link letraDeTipo}: aquella existe para la respuesta de
 * la emisión, que trae `tipo` y **no** trae `letra`. Donde el contrato ya
 * resolvió la letra, se muestra la del contrato — dos formas de averiguar lo
 * mismo son dos formas de que un día no coincidan.
 *
 * Un `tipo` desconocido (no debería pasar) se muestra tal cual, sin inventarle
 * un nombre: mejor un rótulo raro que uno equivocado en un registro fiscal.
 */
export function nombreDeComprobante(tipo: number, letra: string): string {
  switch (tipo) {
    case 1:
    case 6:
    case 11:
      return `Factura ${letra}`;
    case 3:
    case 8:
    case 13:
      return `Nota de crédito ${letra}`;
    case 2:
    case 7:
    case 12:
      return `Nota de débito ${letra}`;
    default:
      return `Comprobante tipo ${tipo} (${letra})`;
  }
}

/**
 * Una fecha del contrato (`aaaa-mm-dd`) escrita como `dd/mm/aaaa`, **sin pasar
 * por `Date`**.
 *
 * # POR QUÉ NO SE USA `new Date(...)`
 *
 * `new Date('2026-08-01')` se interpreta como **medianoche UTC**, y en la
 * Argentina (UTC-3) eso es el 31-jul a las 21:00: cualquier `toLocaleDateString`
 * posterior mostraría **el día anterior**. En una fecha fiscal eso no es un
 * detalle cosmético — es el comprobante corrido de día, y el período al que
 * pertenece.
 *
 * Las fechas ya vienen resueltas en `America/Argentina/Buenos_Aires` desde el
 * backend (`CbteFch` se arma en esa zona). Lo único correcto que puede hacer el
 * front es **reordenar los mismos dígitos**, sin convertir nada: así el
 * resultado no depende de la zona horaria del navegador que abre la pantalla.
 *
 * Lo que no tiene la forma esperada se devuelve tal cual, para que un dato
 * inesperado se vea en vez de desaparecer.
 */
export function fechaArgentina(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!partes) {
    return iso;
  }
  return `${partes[3]}/${partes[2]}/${partes[1]}`;
}

/**
 * Un importe del contrato, con dos decimales y separadores argentinos.
 *
 * **Formatea; no calcula.** Ningún total de la pantalla de listado se arma
 * sumando en el cliente: el resumen del período lo agrega la base
 * (`/consultas/comprobantes/resumen`) y cada fila trae sus propios importes ya
 * redondeados a 2 decimales. Sumar `number` en JavaScript es exactamente cómo
 * se rompe un centavo, y ARCA valida al centavo que `ImpTotal = ImpNeto +
 * ImpIVA`.
 */
export function formateaMoneda(valor: number | null | undefined): string {
  if (valor == null) {
    return '—';
  }
  return valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Cómo se lee el receptor de un comprobante ya emitido: `CUIT 20357540977`, o
 * `Consumidor final` cuando fue anónimo (`docTipo 99`, con `docNro 0` — mostrar
 * "DOC 0" no le diría nada a nadie).
 *
 * El número se arma con `String(...)` y **no** con `toLocaleString`: un CUIT con
 * separadores de miles deja de ser un CUIT que alguien pueda comparar contra un
 * papel.
 *
 * Reusa {@link TIPOS_DE_DOCUMENTO}, la misma tabla que ofrece el formulario de
 * emisión. Un tipo que no esté ahí se muestra con su código, sin inventar.
 */
export function receptorLegible(docTipo: number, docNro: number): string {
  if (docTipo === 99) {
    return 'Consumidor final';
  }
  const conocido = TIPOS_DE_DOCUMENTO.find((t) => t.id === docTipo);
  return `${conocido ? conocido.etiqueta : `Documento tipo ${docTipo}`} ${String(docNro)}`;
}

/**
 * La condición del receptor frente al IVA, traducida con la misma tabla que usa
 * el formulario de emisión ({@link CONDICIONES_IVA_RECEPTOR}).
 *
 * Es el dato que explica **por qué salió esa letra**: la condición que viajó en
 * el comprobante, no la ficha actual del cliente (que pudo cambiar después).
 */
export function etiquetaDeCondicionIva(condicionIva: number): string {
  const conocida = CONDICIONES_IVA_RECEPTOR.find((c) => c.id === condicionIva);
  return conocida ? conocida.etiqueta : `Condición ${condicionIva}`;
}

/** El concepto (`1` productos, `2` servicios, `3` ambos), en palabras. */
export function etiquetaDeConcepto(concepto: number): string {
  const conocido = CONCEPTOS.find((c) => c.id === concepto);
  return conocido ? conocido.etiqueta : `Concepto ${concepto}`;
}

/**
 * `00001-00000016`. Punto de venta a **5** dígitos y número a 8: el mismo
 * padding que ya usa `PdfService.nombreDeArchivo` (`%05d-%08d`) y que
 * `CLAUDE.md` repite en la doctrina del proyecto (p. ej. la Factura A
 * `00001-00000015`) — no el 4+8 que a veces se ve en otros lados.
 */
export function numeroFormateado(puntoVenta: number | null | undefined, numero: number | null | undefined): string {
  if (puntoVenta == null || numero == null) {
    return '—';
  }
  return `${String(puntoVenta).padStart(5, '0')}-${String(numero).padStart(8, '0')}`;
}
